import type { LostReason, Role, Score, Source, Stage, TouchKind } from './types';

export const STAGES: Stage[] = ['new', 'qualified', 'visit_booked', 'visit_done', 'negotiation', 'booked', 'lost', 'dormant'];

export const stageLabel: Record<Stage, string> = {
  new: 'New', qualified: 'Qualified', visit_booked: 'Visit booked', visit_done: 'Visit done',
  negotiation: 'Negotiation', booked: 'Booked', lost: 'Lost', dormant: 'Dormant',
};

export const stageTone: Record<Stage, Tone> = {
  new: 'cold', qualified: 'cold', visit_booked: 'accent', visit_done: 'accent',
  negotiation: 'warn', booked: 'ok', lost: 'bad', dormant: 'muted',
};

export const scoreLabel: Record<Score, string> = { unscored: 'Not scored', hot: 'Hot', warm: 'Warm', cold: 'Cold' };

export const roleLabel: Record<Role, string> = { owner: 'Owner', manager: 'Manager', reviewer: 'Certified reviewer', rep: 'Rep' };

export const sourceLabel: Record<Source, string> = {
  post_link: 'Post link', ctwa_ad: 'Click-to-WhatsApp ad', portal: 'Property portal', website: 'Website form',
  whatsapp_direct: 'WhatsApp, direct', rep_sourced: 'Rep sourced', walk_in: 'Walk-in', builder: 'Shared by builder',
  sub_broker: 'Sub-broker referral', meta_form: 'Meta lead form', call: 'Phone call', manual: 'Added by hand',
};

// Sources a person can pick when adding a lead by hand. The rest arrive automatically.
export const MANUAL_SOURCES: Source[] = ['manual', 'rep_sourced', 'walk_in', 'call', 'builder', 'sub_broker', 'portal', 'website'];

export const lostReasonLabel: Record<LostReason, string> = {
  budget: 'Budget', location: 'Location', possession_date: 'Possession date', other_project: 'Chose another project',
  other_broker: 'Chose another broker', not_genuine: 'Not genuine', unreachable: 'Unreachable',
};

export const touchLabel: Record<TouchKind, string> = { call: 'Call', message: 'Message', visit: 'Site visit', meeting: 'Meeting' };

export type Tone = 'ok' | 'warn' | 'bad' | 'cold' | 'accent' | 'muted';

const IST = 'Asia/Kolkata';

export function formatPhone(e164: string): string {
  if (e164.startsWith('+91') && e164.length === 13) return `+91 ${e164.slice(3, 8)} ${e164.slice(8)}`;
  return e164;
}

export function dateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-IN', { timeZone: IST, day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })
    .format(new Date(iso));
}

export function relative(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return '—';
  const diff = new Date(iso).getTime() - now;
  const abs = Math.abs(diff);
  const mins = Math.round(abs / 60000);
  const unit = mins < 60 ? `${mins} min` : mins < 60 * 24 ? `${Math.round(mins / 60)} h` : `${Math.round(mins / 1440)} d`;
  if (mins < 1) return 'just now';
  return diff < 0 ? `${unit} ago` : `in ${unit}`;
}

export function isOverdue(iso: string | null | undefined, now = Date.now()): boolean {
  return !!iso && new Date(iso).getTime() < now;
}

// "2026-11-03T15:30" in IST for <input type="datetime-local">, and back.
export function toLocalInput(d: Date): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: IST, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '00';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}

export function fromLocalInput(value: string): string {
  return new Date(`${value}:00+05:30`).toISOString();
}

// Friendly text for errors raised by the database functions. Unknown errors are shown as they are.
const ERRORS: Record<string, string> = {
  invalid_phone: "That phone number doesn't look right. Use 10 digits for India, or +country code for other countries.",
  not_signed_in: 'Please sign in again.',
  no_active_membership: "Your account isn't part of an active firm.",
  manager_only: 'Only a manager or owner can do that.',
  owner_only: 'Only an owner can do that.',
  already_member: 'This account already belongs to a firm.',
  invite_invalid: 'This invite link is not valid. It may have expired, been used, or be for a different email.',
  lead_not_found: "You don't have access to that lead.",
  lost_reason_required: 'Choose why the lead was lost.',
  wake_date_required: 'Choose a future date to wake this lead.',
  next_step_needs_date_and_note: 'A next step needs a date and a short note.',
  reason_required: 'Write a reason.',
  owner_not_available: 'That person is on leave or inactive.',
  owner_not_active: 'That person is inactive.',
  project_not_found: 'That project is not available.',
  cannot_change_own_role: "You can't change your own role or deactivate yourself.",
  last_owner: 'The firm needs at least one active owner.',
  empty_note: 'Write something first.',
  empty_message: 'Write a message first.',
  window_closed: "The buyer hasn't messaged in the last 24 hours, so WhatsApp only allows approved templates. Templates arrive in week 3. Call them instead for now.",
  whatsapp_not_connected: "This firm's WhatsApp number isn't connected yet, so real replies can't be sent. Test leads from the simulator work.",
  source_not_trackable: 'That kind of source cannot have a tracked link.',
  reviewer_only: 'Only managers and certified reviewers can approve AI replies.',
  draft_stale: 'The price sheet changed after this reply was written, so it cannot be sent. Reject it and reply yourself.',
  draft_not_held: 'This reply has already been handled.',
  still_risky: 'The reply still contains a phrase that can never be sent',
  fact_not_draft: 'This fact has already been reviewed.',
  as_of_required: 'Choose the date of the new price sheet.',
  visit_time_invalid: 'Choose a visit time in the future.',
  visit_outcome_required: 'Choose how the visit went.',
  visit_already_recorded: 'This visit has already been recorded.',
  use_stage_screen: 'To mark a lead lost or dormant, use the Stage button so the reason is recorded.',
  test_leads_only: 'Moving the clock only works on test leads from the simulator.',
  already_assigned: 'This lead already has an owner.',
  period_invalid: 'Choose a valid period.',
  cta_not_deliverable: 'That call to action cannot be promised for this project',
  post_not_ready: 'This post is not ready to share yet.',
  post_not_waiting: 'Only posts waiting for review can be approved.',
  lead_needs_project: 'Choose the project on this lead first.',
  registration_already_live: 'This buyer already has a live registration for this project.',
  sent_date_invalid: 'Choose when it was sent: not in the future, and within the last 60 days.',
  registration_text_required: 'The registration message cannot be empty.',
  registration_not_waiting: 'Only a registration waiting for the builder can be acknowledged.',
  acknowledged_date_invalid: 'The acknowledgement date must be after it was sent and not in the future.',
  registration_closed: 'This registration is already closed.',
  file_path_invalid: 'The file could not be filed against this registration.',
  lead_not_open: 'This lead is lost or dormant. Reopen it before recording a booking.',
  booked_date_invalid: 'Choose the booking date: not in the future.',
  booking_exists: 'This lead already has a booking. Cancel it first to record a new one.',
  milestones_must_total_100: 'The milestone shares must add up to 100%.',
  milestones_invalid: 'Add between one and six milestones.',
  invoice_date_invalid: 'The invoice date must be on or after the booking date and not in the future.',
  invoice_needs_number_and_date: 'An invoice needs both a number and a date.',
  received_date_invalid: 'The received date must be on or after the booking date and not in the future.',
  amount_required: 'Enter the amount received.',
  correction_needs_note: 'A correction (a negative amount) needs a note saying why.',
  booking_cancelled: 'This booking is cancelled.',
  text_required: 'Paste the enquiry email first.',
  name_required: 'Enter the firm name.',
  kit_invalid: 'The AI did not write a usable brief. Try Write again.',
  text_too_long: 'That is too long. Paste one enquiry at a time.',
};

export function friendlyError(e: unknown): string {
  const msg = e instanceof Error ? e.message : typeof e === 'object' && e && 'message' in e ? String((e as { message: unknown }).message) : String(e);
  for (const [code, text] of Object.entries(ERRORS)) if (msg.includes(code)) return text;
  if (msg.includes('duplicate key') && msg.includes('teams')) return 'A team with that name already exists.';
  if (msg.includes('duplicate key') && msg.includes('projects')) return 'A project with that name already exists.';
  return msg;
}

export const factCategoryLabel: Record<string, string> = {
  overview: 'Overview', unit: 'Units and sizes', price: 'Prices', payment_plan: 'Payment plan', possession: 'Possession',
  amenity: 'Amenities', location: 'Location', legal: 'Legal and RERA', offer: 'Offers', other: 'Other',
};

export function countdown(iso: string | null | undefined, now = Date.now()): { text: string; late: boolean } | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - now;
  const mins = Math.round(Math.abs(ms) / 60000);
  const t = mins < 60 ? `${mins} min` : `${Math.floor(mins / 60)} h ${mins % 60} min`;
  return ms >= 0 ? { text: `Respond within ${t}`, late: false } : { text: `Response ${t} late`, late: true };
}
export const outcomeLabel: Record<string, string> = { liked: 'Liked it', thinking: 'Thinking it over', rejected: 'Not interested' };
export function inr(n: number | null | undefined): string {
  if (!n) return '—';
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2).replace(/\.?0+$/, '')} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(1).replace(/\.0$/, '')} L`;
  return `₹${n.toLocaleString('en-IN')}`;
}

export const ctaLabel: Record<string, string> = {
  price_sheet: 'Get the price sheet', site_visit: 'Book a site visit', availability: 'Check availability',
  callback: 'Get a call back', video_tour: 'Video walkthrough (NRI)', offer: 'Claim the offer',
};
export const channelLabel: Record<string, string> = {
  whatsapp_status: 'WhatsApp Status', facebook: 'Facebook post', instagram: 'Instagram post', broker_group: 'Broker group',
  whatsapp_campaign: 'WhatsApp campaign draft', facebook_ad: 'Facebook ad copy', instagram_ad: 'Instagram ad copy',
};
export function minutes(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  const v = Number(n);
  return v < 60 ? `${v.toFixed(v < 10 ? 1 : 0)} min` : `${(v / 60).toFixed(1)} h`;
}

// Copy text even where the browser blocks the clipboard API (some in-app browsers, old Android WebViews).
export async function copyText(text: string): Promise<boolean> {
  try { await navigator.clipboard.writeText(text); return true; } catch { /* fall back below */ }
  try {
    const t = document.createElement('textarea');
    t.value = text; t.setAttribute('readonly', ''); t.style.position = 'fixed'; t.style.opacity = '0';
    document.body.appendChild(t); t.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(t);
    return ok;
  } catch { return false; }
}

export const registrationStatusLabel: Record<string, string> = {
  sent: 'Waiting for the builder', acknowledged: 'Acknowledged', rejected: 'Rejected by the builder', withdrawn: 'Withdrawn',
};
export const registrationChannelLabel: Record<string, string> = {
  email: 'Email', whatsapp: 'WhatsApp', builder_portal: "Builder's portal", in_person: 'In person',
};
export const proofKindLabel: Record<string, string> = {
  sent_copy: 'Copy of what was sent', acknowledgement: "Builder's acknowledgement", visit_sheet: 'Signed visit sheet', other: 'Other proof',
};
export const ageBucketLabel: Record<string, string> = {
  not_invoiced: 'Not invoiced yet', '0_30': '0–30 days', '31_60': '31–60 days', '61_90': '61–90 days', over_90: 'Over 90 days',
};
// Full rupees in Indian grouping: ₹4,11,800.
export function rupees(n: number | null | undefined): string {
  const v = Math.round(Number(n ?? 0));
  return `${v < 0 ? '−' : ''}₹${Math.abs(v).toLocaleString('en-IN')}`;
}
export function dateOnly(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00+05:30` : iso);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });
}
export function istToday(offsetDays = 0): string {
  return new Date(Date.now() + 5.5 * 3600e3 + offsetDays * 86400e3).toISOString().slice(0, 10);
}
